import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { adminApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import RichTextEditor from '../components/RichTextEditor';
import { AdminAnalyticsTab } from '../components/admin/AdminAnalyticsTab';
import { AdminCoverageTab } from '../components/admin/AdminCoverageTab';
import { AdminBackupRestore } from '../components/admin/AdminBackupRestore';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Users, 
  Plane, 
  MapPin, 
  TrendingUp,
  Search,
  Eye,
  Pencil,
  Trash2,
  Shield,
  Crown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BarChart3,
  UserCheck,
  Calendar,
  Gift,
  FileText,
  Plus,
  UserPlus,
  Globe,
  Map as MapIcon,
  EyeOff,
  Mail,
  KeyRound,
  Sparkles,
  Languages
} from 'lucide-react';

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [missions, setMissions] = useState([]);
  const [drones, setDrones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ email: '', password: '', name: '', role: 'admin' });
  const [creatingUser, setCreatingUser] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  // ED-269 UAS zones
  const [ed269Country, setEd269Country] = useState('it');
  const [ed269File, setEd269File] = useState(null);
  const [ed269Uploading, setEd269Uploading] = useState(false);
  const [ed269Summary, setEd269Summary] = useState(null);
  const [ed269Countries, setEd269Countries] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalMissions, setTotalMissions] = useState(0);
  const [totalDrones, setTotalDrones] = useState(0);
  
  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [blogDialogOpen, setBlogDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Réglementation',
    tags: '',
    published: false,
    meta_description: '',
    cover_image: ''
  });
  // Blog cover-image UX state — kept separate from the form so we can
  // reflect loading spinners without leaking transient state into the
  // saved payload.
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverGenerating, setCoverGenerating] = useState(false);

  // AI Generation state
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiCategoryHint, setAiCategoryHint] = useState('Réglementation');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiMode, setAiMode] = useState('single'); // 'single' | 'batch'
  const [aiBatchPrompts, setAiBatchPrompts] = useState('');
  const [aiBatchResults, setAiBatchResults] = useState(null);
  const [translatingPostId, setTranslatingPostId] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Accès refusé - Administrateur requis');
      navigate('/dashboard');
      return;
    }
    loadData();
    loadEd269Countries();
  }, [user, navigate]);

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'missions') {
      loadMissions();
    } else if (activeTab === 'drones') {
      loadDrones();
    } else if (activeTab === 'blog') {
      loadBlogPosts();
    }
  }, [activeTab, searchTerm, planFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getStats();
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accès refusé');
        navigate('/dashboard');
      } else {
        toast.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (planFilter && planFilter !== 'all') params.plan = planFilter;
      
      const response = await adminApi.getUsers(params);
      setUsers(response.data.users);
      setTotalUsers(response.data.total);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    }
  };

  const loadMissions = async () => {
    try {
      const response = await adminApi.getMissions({ limit: 100 });
      setMissions(response.data.missions);
      setTotalMissions(response.data.total);
    } catch (error) {
      toast.error('Erreur lors du chargement des missions');
    }
  };

  const loadDrones = async () => {
    try {
      const response = await adminApi.getDrones({ limit: 100 });
      setDrones(response.data.drones);
      setTotalDrones(response.data.total);
    } catch (error) {
      toast.error('Erreur lors du chargement des drones');
    }
  };

  const loadBlogPosts = async () => {
    try {
      const response = await adminApi.getBlogPosts({ limit: 100 });
      setBlogPosts(response.data.posts || []);
      setTotalPosts(response.data.total || 0);
    } catch (error) {
      toast.error('Erreur lors du chargement des articles');
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const openNewPostDialog = () => {
    setEditingPost(null);
    setBlogForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: 'Réglementation',
      tags: '',
      published: false,
      meta_description: '',
      cover_image: ''
    });
    setBlogDialogOpen(true);
  };

  const openEditPostDialog = (post) => {
    setEditingPost(post);
    setBlogForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      tags: post.tags?.join(', ') || '',
      published: post.published,
      meta_description: post.meta_description || '',
      cover_image: post.cover_image || ''
    });
    setBlogDialogOpen(true);
  };

  const handleSavePost = async () => {
    try {
      const postData = {
        ...blogForm,
        tags: blogForm.tags.split(',').map(t => t.trim()).filter(t => t)
      };
      // Send empty string (not null) so the update endpoint — which
      // filters out None values — actually persists the removal. Read-side
      // treats "" as "no cover".
      if (!postData.cover_image) postData.cover_image = '';

      if (editingPost) {
        await adminApi.updateBlogPost(editingPost.id, postData);
        toast.success('Article mis à jour');
      } else {
        await adminApi.createBlogPost(postData);
        toast.success('Article créé');
      }
      setBlogDialogOpen(false);
      loadBlogPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  // Cover-image handlers.
  //   • upload: reads a local file and POSTs multipart → backend compresses
  //     & returns a base64 data URL.
  //   • generate: sends the article title as prompt (or falls back to the
  //     user-typed text) → backend runs Nano Banana + compresses.
  //   • clear: wipes the cover_image field on the form (persisted on save).
  const handleUploadCover = async (file) => {
    if (!file) return;
    if (file.size > 1_500_000) {
      toast.error(`Image trop lourde (${(file.size / 1024).toFixed(0)} KB, max 1500 KB)`);
      return;
    }
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await adminApi.uploadBlogImage(fd);
      setBlogForm((f) => ({ ...f, cover_image: data.cover_image }));
      toast.success('Image de couverture chargée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur upload');
    } finally {
      setCoverUploading(false);
    }
  };

  const handleGenerateCover = async () => {
    // Prefer the article title as the illustration subject; if empty, ask
    // the admin for a manual prompt so we never send a blank generation.
    let prompt = blogForm.title?.trim();
    if (!prompt) {
      prompt = window.prompt('Décris l\'image souhaitée (ex : "drone survolant les Alpes au coucher du soleil")');
      if (!prompt) return;
    }
    setCoverGenerating(true);
    try {
      const { data } = await adminApi.generateBlogImage({ prompt });
      setBlogForm((f) => ({ ...f, cover_image: data.cover_image }));
      toast.success('Image générée par IA');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur génération IA');
    } finally {
      setCoverGenerating(false);
    }
  };

  const handleCoverUrl = () => {
    const url = window.prompt('Colle l\'URL publique de l\'image (https://...)', blogForm.cover_image || '');
    if (url === null) return;
    setBlogForm((f) => ({ ...f, cover_image: url.trim() }));
  };

  const handleClearCover = () => {
    setBlogForm((f) => ({ ...f, cover_image: '' }));
  };

  const handleGenerateWithAI = async () => {
    if (!aiPrompt.trim() || aiPrompt.trim().length < 5) {
      toast.error('Le sujet doit contenir au moins 5 caractères');
      return;
    }
    setAiGenerating(true);
    try {
      const { data } = await adminApi.generateBlogFromPrompt({
        prompt: aiPrompt.trim(),
        category_hint: aiCategoryHint,
      });
      const draft = data.draft;
      // Pre-fill the editing form with the AI draft
      setEditingPost(null);
      setBlogForm({
        title: draft.title || '',
        slug: draft.slug || '',
        excerpt: draft.excerpt || '',
        content: draft.content || '',
        category: draft.category || 'Réglementation',
        tags: Array.isArray(draft.tags) ? draft.tags.join(', ') : '',
        published: false,
        meta_description: draft.meta_description || '',
        cover_image: '',
      });
      setAiDialogOpen(false);
      setAiPrompt('');
      setBlogDialogOpen(true);
      toast.success('Brouillon généré ✨ — Relisez avant de publier');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de génération IA');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleTranslateAll = async (postId) => {
    setTranslatingPostId(postId);
    try {
      const { data } = await adminApi.translateBlogPostAll(postId);
      const ok = data.success;
      const fail = data.failed;
      if (fail === 0) {
        toast.success(`Article traduit en ${ok} langue(s) ✨`);
      } else {
        toast.warning(`${ok}/${data.total} traductions OK — ${fail} échec(s)`);
      }
      loadBlogPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de traduction IA');
    } finally {
      setTranslatingPostId(null);
    }
  };

  const handleGenerateBatch = async () => {
    const lines = aiBatchPrompts
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length >= 5);
    if (lines.length === 0) {
      toast.error('Saisissez au moins un sujet (1 par ligne, min 5 caractères)');
      return;
    }
    if (lines.length > 15) {
      toast.error('Maximum 15 sujets par lot');
      return;
    }
    setAiGenerating(true);
    setAiBatchResults(null);
    try {
      const { data } = await adminApi.generateBlogBatch({
        prompts: lines,
        category_hint: aiCategoryHint,
        auto_save_as_draft: true,
      });
      setAiBatchResults(data);
      toast.success(`Lot terminé : ${data.success}/${data.total} articles créés en brouillon`);
      // Refresh the blog posts list to show the new drafts
      loadBlogPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de génération en lot');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm('Supprimer cet article ?')) return;
    try {
      await adminApi.deleteBlogPost(postId);
      toast.success('Article supprimé');
      loadBlogPosts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      await adminApi.updateBlogPost(post.id, { published: !post.published });
      toast.success(post.published ? 'Article dépublié' : 'Article publié');
      loadBlogPosts();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleViewUser = async (userId) => {
    try {
      const response = await adminApi.getUser(userId);
      setSelectedUser(response.data);
      setUserDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'utilisateur');
    }
  };

  const handleUpdateUserPlan = async (userId, newPlan) => {
    try {
      await adminApi.updateUser(userId, { subscription_status: newPlan });
      toast.success('Plan mis à jour');
      loadUsers();
      if (selectedUser?.id === userId) {
        setSelectedUser({ ...selectedUser, subscription_status: newPlan });
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleToggleAdmin = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    try {
      await adminApi.updateUser(userId, { role: newRole });
      toast.success(`Utilisateur ${newRole === 'admin' ? 'promu admin' : 'rétrogradé'}`);
      loadUsers();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleResetPassword = async (userId, email) => {
    if (!window.confirm(`Envoyer un email de réinitialisation de mot de passe à ${email} ?`)) return;
    
    try {
      await adminApi.resetUserPassword(userId);
      toast.success(`Email de réinitialisation envoyé à ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email');
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Supprimer l'utilisateur ${email} et toutes ses données ?`)) return;
    
    try {
      await adminApi.deleteUser(userId);
      toast.success('Utilisateur supprimé');
      loadUsers();
      setUserDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const loadEd269Countries = async () => {
    try {
      const res = await adminApi.getUasZonesCountries();
      setEd269Countries(res.data.countries || []);
    } catch (_) { /* silent */ }
  };

  const handleEd269Upload = async (e) => {
    e?.preventDefault?.();
    if (!ed269File) {
      toast.error('Sélectionne d\'abord un fichier ED-269 JSON');
      return;
    }
    if (!/^[a-z]{2}$/.test(ed269Country)) {
      toast.error('Code pays ISO-2 invalide (ex: it, es, pl, pt)');
      return;
    }
    setEd269Uploading(true);
    setEd269Summary(null);
    try {
      const res = await adminApi.uploadEd269(ed269Country, ed269File, true);
      setEd269Summary(res.data);
      toast.success(`${res.data.imported} zones UAS importées pour ${res.data.country}`);
      setEd269File(null);
      await loadEd269Countries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import ED-269');
    } finally {
      setEd269Uploading(false);
    }
  };

  const handleEd269Delete = async (country) => {
    if (!window.confirm(`Supprimer toutes les zones UAS importées pour ${country.toUpperCase()} ?`)) return;
    try {
      const res = await adminApi.deleteEd269(country);
      toast.success(`${res.data.deleted} zones supprimées pour ${country.toUpperCase()}`);
      await loadEd269Countries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDownloadBackup = async () => {
    try {
      const res = await adminApi.exportBackup();
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.download = `drone-maps-backup-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup téléchargé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setDownloadingBackup(false);
    }
  };

  const handleCreateUser = async (e) => {
    e?.preventDefault?.();
    if (!newUserForm.email || !newUserForm.password) {
      toast.error('Email et mot de passe requis');
      return;
    }
    if (newUserForm.password.length < 8) {
      toast.error('Mot de passe : 8 caractères minimum');
      return;
    }
    setCreatingUser(true);
    try {
      const res = await adminApi.createUser(newUserForm);
      toast.success(`Compte ${res.data.user.role === 'admin' ? 'admin ' : ''}${res.data.user.email} créé`);
      setCreateUserDialogOpen(false);
      setNewUserForm({ email: '', password: '', name: '', role: 'admin' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreatingUser(false);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'coverage',  label: 'Couverture pays', icon: MapIcon },
    { id: 'users', label: 'Utilisateurs', icon: Users },
    { id: 'missions', label: 'Missions', icon: MapPin },
    { id: 'drones', label: 'Drones', icon: Plane },
    { id: 'blog', label: 'Blog', icon: FileText },
  ];

  if (loading && !stats) {
    return (
      <DashboardLayout title="Administration">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Administration" data-testid="admin-page">
      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200 pb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg transition-colors
              ${activeTab === tab.id 
                ? 'bg-sky-500 text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}
            `}
            data-testid={`admin-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && stats && (
        <div className="space-y-6">
          {/* Backup banner */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid="admin-backup-banner">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">Backup manuel de la base</div>
                <div className="text-sm text-slate-600">Télécharge un dump JSON complet (utilisateurs, missions, drones, blog). Stocke-le dans un endroit sûr.</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <AdminBackupRestore token={localStorage.getItem('dronemaps_token')} />
              <Button
                type="button"
                onClick={handleDownloadBackup}
                disabled={downloadingBackup}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="admin-download-backup-btn"
              >
                {downloadingBackup ? 'Export en cours...' : 'Télécharger le backup'}
              </Button>
            </div>
          </div>

          {/* ED-269 UAS zones importer */}
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="admin-ed269-section">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-sky-600" />
                  <h3 className="font-semibold text-slate-900 text-lg">Zones UAS officielles (ED-269)</h3>
                </div>
                <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                  Importe le fichier JSON ED-269 officiel publié par chaque NAA (D-Flight IT, AESA ES, PANSA PL, ANAC PT…). Les zones seront affichées sur la carte et utilisées par le moteur de conformité.
                </p>
              </div>
              {ed269Countries.length > 0 && (
                <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">
                  Pays importés :
                  <div className="mt-1 flex flex-wrap gap-1">
                    {ed269Countries.map((c) => (
                      <span key={c.country} className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded px-2 py-0.5 text-slate-700">
                        <strong>{c.country}</strong> <span className="text-slate-400">· {c.count} zones</span>
                        <button
                          type="button"
                          onClick={() => handleEd269Delete(c.country)}
                          className="ml-1 text-red-500 hover:text-red-700"
                          title="Supprimer"
                          data-testid={`admin-ed269-delete-${c.country.toLowerCase()}`}
                        >×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={handleEd269Upload} className="flex flex-wrap items-end gap-3">
              <div>
                <Label htmlFor="ed269-country" className="text-xs">Pays (ISO-2)</Label>
                <Select value={ed269Country} onValueChange={setEd269Country}>
                  <SelectTrigger id="ed269-country" className="w-[120px]" data-testid="admin-ed269-country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">🇫🇷 FR — France</SelectItem>
                    <SelectItem value="de">🇩🇪 DE — Allemagne</SelectItem>
                    <SelectItem value="es">🇪🇸 ES — Espagne</SelectItem>
                    <SelectItem value="it">🇮🇹 IT — Italie</SelectItem>
                    <SelectItem value="pt">🇵🇹 PT — Portugal</SelectItem>
                    <SelectItem value="be">🇧🇪 BE — Belgique</SelectItem>
                    <SelectItem value="lu">🇱🇺 LU — Luxembourg</SelectItem>
                    <SelectItem value="nl">🇳🇱 NL — Pays-Bas</SelectItem>
                    <SelectItem value="ch">🇨🇭 CH — Suisse</SelectItem>
                    <SelectItem value="at">🇦🇹 AT — Autriche</SelectItem>
                    <SelectItem value="cz">🇨🇿 CZ — Tchéquie</SelectItem>
                    <SelectItem value="sk">🇸🇰 SK — Slovaquie</SelectItem>
                    <SelectItem value="pl">🇵🇱 PL — Pologne</SelectItem>
                    <SelectItem value="ro">🇷🇴 RO — Roumanie</SelectItem>
                    <SelectItem value="dk">🇩🇰 DK — Danemark</SelectItem>
                    <SelectItem value="se">🇸🇪 SE — Suède</SelectItem>
                    <SelectItem value="fi">🇫🇮 FI — Finlande</SelectItem>
                    <SelectItem value="no">🇳🇴 NO — Norvège</SelectItem>
                    <SelectItem value="is">🇮🇸 IS — Islande</SelectItem>
                    <SelectItem value="ie">🇮🇪 IE — Irlande</SelectItem>
                    <SelectItem value="ee">🇪🇪 EE — Estonie</SelectItem>
                    <SelectItem value="cy">🇨🇾 CY — Chypre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[240px]">
                <Label htmlFor="ed269-file" className="text-xs">Fichier ED-269 JSON</Label>
                <Input
                  id="ed269-file"
                  type="file"
                  accept="application/json,.json"
                  onChange={(e) => setEd269File(e.target.files?.[0] || null)}
                  data-testid="admin-ed269-file"
                />
              </div>
              <Button
                type="submit"
                disabled={ed269Uploading || !ed269File}
                className="bg-sky-500 hover:bg-sky-600 text-white"
                data-testid="admin-ed269-upload-btn"
              >
                {ed269Uploading ? 'Import en cours...' : 'Importer'}
              </Button>
            </form>

            {ed269Summary && (
              <div className="mt-4 text-sm bg-green-50 border border-green-200 rounded-lg p-3 text-green-800" data-testid="admin-ed269-summary">
                ✅ <strong>{ed269Summary.imported}</strong> zones importées pour <strong>{ed269Summary.country}</strong>.
                {ed269Summary.summary?.by_type && (
                  <span className="ml-2 text-slate-600">
                    Répartition : {Object.entries(ed269Summary.summary.by_type).map(([t, n]) => `${t}: ${n}`).join(' · ')}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* KPI Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-sky-600" />
                </div>
                <span className="text-sm text-slate-500">Utilisateurs</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats.total_users}</p>
              <p className="text-xs text-emerald-600 mt-1">+{stats.new_users_week} cette semaine</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
                  <Crown className="w-5 h-5 text-sky-600" />
                </div>
                <span className="text-sm text-slate-500">Abonnés Solo</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats.users_by_plan?.solo || 0}</p>
              <p className="text-xs text-slate-500 mt-1">
                {stats.users_by_plan?.partner || 0} partenaires • {stats.users_by_plan?.free || 0} gratuits
              </p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-violet-600" />
                </div>
                <span className="text-sm text-slate-500">Missions</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats.total_missions}</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Plane className="w-5 h-5 text-amber-600" />
                </div>
                <span className="text-sm text-slate-500">Drones</span>
              </div>
              <p className="text-3xl font-bold text-slate-900">{stats.total_drones}</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Risk Distribution */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Répartition des risques
              </h3>
              <div className="space-y-3">
                {Object.entries(stats.risk_distribution || {}).map(([level, count]) => (
                  <div key={level} className="flex items-center gap-3">
                    <span className={`
                      w-20 text-xs font-medium px-2 py-1 rounded
                      ${level === 'LOW' ? 'bg-emerald-100 text-emerald-700' :
                        level === 'MODERATE' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'}
                    `}>
                      {level}
                    </span>
                    <div className="flex-1 bg-slate-100 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full ${
                          level === 'LOW' ? 'bg-emerald-500' :
                          level === 'MODERATE' ? 'bg-amber-500' :
                          'bg-red-500'
                        }`}
                        style={{ 
                          width: `${stats.total_missions > 0 ? (count / stats.total_missions) * 100 : 0}%` 
                        }}
                      />
                    </div>
                    <span className="text-sm text-slate-600 w-12 text-right">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CE Class Distribution */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Plane className="w-5 h-5 text-sky-500" />
                Classes CE des drones
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(stats.ce_class_distribution || {}).map(([ceClass, count]) => (
                  <div key={ceClass} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="font-mono font-semibold text-slate-700">{ceClass}</span>
                    <span className="text-sm text-slate-500">{count} drones</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Missions by day */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-violet-500" />
              Missions des 7 derniers jours
            </h3>
            <div className="flex items-end gap-2 h-32">
              {stats.missions_by_day?.slice().reverse().map((day, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                  <div 
                    className="w-full bg-violet-500 rounded-t"
                    style={{ 
                      height: `${Math.max(4, (day.count / Math.max(...stats.missions_by_day.map(d => d.count), 1)) * 100)}%`,
                      minHeight: '4px'
                    }}
                  />
                  <span className="text-xs text-slate-500">
                    {new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'short' })}
                  </span>
                  <span className="text-xs font-semibold text-slate-700">{day.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <AdminAnalyticsTab token={localStorage.getItem('dronemaps_token')} />
      )}

      {/* Coverage Tab */}
      {activeTab === 'coverage' && <AdminCoverageTab />}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par email ou nom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="admin-search-users"
                />
              </div>
            </div>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrer par plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les plans</SelectItem>
                <SelectItem value="free">Gratuit</SelectItem>
                <SelectItem value="solo">Solo</SelectItem>
                <SelectItem value="partner">Partenaire</SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              onClick={() => setCreateUserDialogOpen(true)}
              className="bg-sky-500 hover:bg-sky-600 text-white"
              data-testid="admin-create-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Créer un utilisateur
            </Button>
          </div>

          <p className="text-sm text-slate-500">{totalUsers} utilisateur(s) trouvé(s)</p>

          {/* Users Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Utilisateur</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Plan</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Missions</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Drones</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Inscrit le</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {u.role === 'admin' && (
                          <Shield className="w-4 h-4 text-amber-500" title="Admin" />
                        )}
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`
                        px-2 py-1 rounded text-xs font-medium
                        ${u.subscription_status === 'solo' ? 'bg-sky-100 text-sky-700' : 
                          u.subscription_status === 'partner' ? 'bg-emerald-100 text-emerald-700' : 
                          'bg-slate-100 text-slate-600'}
                      `}>
                        {u.subscription_status === 'solo' ? 'Solo' : 
                         u.subscription_status === 'partner' ? 'Partenaire' : 'Gratuit'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{u.missions_count}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{u.drones_count}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {new Date(u.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleViewUser(u.id)}
                          className="p-2 text-slate-400 hover:text-sky-500 hover:bg-sky-50 rounded"
                          title="Voir détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleUpdateUserPlan(u.id, u.subscription_status === 'solo' ? 'free' : 'solo')}
                          className={`p-2 rounded ${u.subscription_status === 'solo' ? 'text-sky-500 hover:bg-sky-50' : 'text-slate-400 hover:text-sky-500 hover:bg-sky-50'}`}
                          title={u.subscription_status === 'solo' ? 'Rétrograder en Gratuit' : 'Passer en Solo'}
                        >
                          <Crown className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleUpdateUserPlan(u.id, u.subscription_status === 'partner' ? 'free' : 'partner')}
                          className={`p-2 rounded ${u.subscription_status === 'partner' ? 'text-emerald-500 hover:bg-emerald-50' : 'text-slate-400 hover:text-emerald-500 hover:bg-emerald-50'}`}
                          title={u.subscription_status === 'partner' ? 'Retirer partenaire' : 'Définir comme Partenaire'}
                        >
                          <Gift className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleResetPassword(u.id, u.email)}
                          className="p-2 text-slate-400 hover:text-sky-500 hover:bg-sky-50 rounded"
                          title="Réinitialiser mot de passe"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleAdmin(u.id, u.role)}
                          className={`p-2 rounded ${u.role === 'admin' ? 'text-amber-500 hover:bg-amber-50' : 'text-slate-400 hover:text-amber-500 hover:bg-amber-50'}`}
                          title={u.role === 'admin' ? 'Retirer admin' : 'Promouvoir admin'}
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(u.id, u.email)}
                          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Missions Tab */}
      {activeTab === 'missions' && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">{totalMissions} mission(s) au total</p>
          
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Mission</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Utilisateur</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Drone</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Catégorie</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Risque</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Date</th>
                </tr>
              </thead>
              <tbody>
                {missions.map((m) => (
                  <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{m.mission_type}</p>
                      <p className="text-xs text-slate-500">{m.mission_date} à {m.mission_time}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-900">{m.user_name}</p>
                      <p className="text-xs text-slate-500">{m.user_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-900">{m.drone_model}</p>
                      <p className="text-xs text-slate-500">Classe {m.drone_class}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`
                        px-2 py-1 rounded text-xs font-medium
                        ${m.regulation_result?.category?.includes('OPEN') ? 'bg-emerald-100 text-emerald-700' :
                          m.regulation_result?.category?.includes('SPECIFIC') ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'}
                      `}>
                        {m.regulation_result?.sub_category || m.regulation_result?.category || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`
                        px-2 py-1 rounded text-xs font-medium
                        ${m.regulation_result?.risk_level === 'LOW' ? 'bg-emerald-100 text-emerald-700' :
                          m.regulation_result?.risk_level === 'MODERATE' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'}
                      `}>
                        {m.regulation_result?.risk_level || 'N/A'} ({m.risk_score})
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {new Date(m.created_at).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Drones Tab */}
      {activeTab === 'drones' && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">{totalDrones} drone(s) au total</p>
          
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Drone</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Propriétaire</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Classe</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Poids</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Missions</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Ajouté le</th>
                </tr>
              </thead>
              <tbody>
                {drones.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{d.model_name}</p>
                      {d.serial_number && (
                        <p className="text-xs text-slate-500 font-mono">{d.serial_number}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-900">{d.user_name}</p>
                      <p className="text-xs text-slate-500">{d.user_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs font-mono font-medium">
                        {d.ce_class}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{d.weight_kg} kg</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{d.missions_count}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {new Date(d.created_at).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Blog Tab */}
      {activeTab === 'blog' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-slate-900">Articles de blog ({totalPosts})</h2>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setAiDialogOpen(true)}
                className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white shadow-md"
                data-testid="btn-generate-ai-blog"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Générer avec l'IA
              </Button>
              <Button onClick={openNewPostDialog} className="bg-sky-500 hover:bg-sky-600">
                <Plus className="w-4 h-4 mr-2" />
                Nouvel article
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Titre</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Catégorie</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Statut</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Date</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {blogPosts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                      Aucun article. Cliquez sur "Nouvel article" pour commencer.
                    </td>
                  </tr>
                ) : (
                  blogPosts.map((post) => (
                    <tr key={post.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{post.title}</p>
                        <p className="text-xs text-slate-400">/blog/{post.slug}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 rounded bg-sky-100 text-sky-700 text-xs font-medium">
                          {post.category}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {post.published ? (
                          <span className="flex items-center gap-1 text-emerald-600 text-sm">
                            <Globe className="w-4 h-4" />
                            Publié
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-slate-400 text-sm">
                            <EyeOff className="w-4 h-4" />
                            Brouillon
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {new Date(post.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          {post.published && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                              title="Voir"
                            >
                              <Eye className="w-4 h-4 text-slate-500" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTogglePublish(post)}
                            title={post.published ? 'Dépublier' : 'Publier'}
                          >
                            {post.published ? (
                              <EyeOff className="w-4 h-4 text-amber-500" />
                            ) : (
                              <Globe className="w-4 h-4 text-emerald-500" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTranslateAll(post.id)}
                            title="Traduire en 4 langues (EN, DE, IT, ES)"
                            disabled={translatingPostId === post.id}
                            data-testid={`translate-post-${post.id}`}
                          >
                            {translatingPostId === post.id ? (
                              <span className="animate-spin w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full" />
                            ) : (
                              <Languages className="w-4 h-4 text-violet-500" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditPostDialog(post)}
                            title="Modifier"
                          >
                            <Pencil className="w-4 h-4 text-sky-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePost(post.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Detail Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Détails de l'utilisateur</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-sky-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{selectedUser.name}</h3>
                  <p className="text-slate-500">{selectedUser.email}</p>
                  <div className="flex gap-2 mt-1">
                    <span className={`
                      px-2 py-0.5 rounded text-xs font-medium
                      ${selectedUser.subscription_status === 'solo' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}
                    `}>
                      {selectedUser.subscription_status === 'solo' ? 'Solo' : 'Gratuit'}
                    </span>
                    {selectedUser.role === 'admin' && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                        Admin
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Missions</p>
                  <p className="text-2xl font-bold text-slate-900">{selectedUser.missions_count}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Drones</p>
                  <p className="text-2xl font-bold text-slate-900">{selectedUser.drones_count}</p>
                </div>
              </div>

              <div className="text-sm text-slate-500">
                Inscrit le {new Date(selectedUser.created_at).toLocaleDateString('fr-FR')}
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => handleUpdateUserPlan(selectedUser.id, selectedUser.subscription_status === 'solo' ? 'free' : 'solo')}
                  className="flex-1"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  {selectedUser.subscription_status === 'solo' ? 'Rétrograder' : 'Passer en Solo'}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteUser(selectedUser.id, selectedUser.email)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Blog Post Dialog */}
      <Dialog open={blogDialogOpen} onOpenChange={setBlogDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPost ? 'Modifier l\'article' : 'Nouvel article'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="title">Titre *</Label>
              <Input
                id="title"
                value={blogForm.title}
                onChange={(e) => {
                  setBlogForm({ 
                    ...blogForm, 
                    title: e.target.value,
                    slug: editingPost ? blogForm.slug : generateSlug(e.target.value)
                  });
                }}
                placeholder="Titre de l'article"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="slug">Slug (URL) *</Label>
              <Input
                id="slug"
                value={blogForm.slug}
                onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })}
                placeholder="mon-article"
                className="mt-1"
              />
              <p className="text-xs text-slate-400 mt-1">URL: /blog/{blogForm.slug}</p>
            </div>
            
            <div>
              <Label htmlFor="category">Catégorie</Label>
              <Select
                value={blogForm.category}
                onValueChange={(value) => setBlogForm({ ...blogForm, category: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Réglementation">Réglementation</SelectItem>
                  <SelectItem value="Tutoriels">Tutoriels</SelectItem>
                  <SelectItem value="Actualités">Actualités</SelectItem>
                  <SelectItem value="Équipement">Équipement</SelectItem>
                  <SelectItem value="Cas d'usage">Cas d'usage</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="excerpt">Extrait / Résumé *</Label>
              <Textarea
                id="excerpt"
                value={blogForm.excerpt}
                onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                placeholder="Résumé de l'article (affiché dans la liste)"
                rows={2}
                className="mt-1"
              />
            </div>

            {/* Cover image — 3 sources: upload local file, paste URL, or
                generate with Nano Banana. Persisted as a string (URL or
                base64 data URL) in the `cover_image` field. */}
            <div>
              <Label>Image de couverture</Label>
              <div className="mt-1 flex gap-4 items-start" data-testid="blog-cover-block">
                <div className="w-48 h-28 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                  {blogForm.cover_image ? (
                    <img
                      src={blogForm.cover_image}
                      alt="Aperçu couverture"
                      className="w-full h-full object-cover"
                      data-testid="blog-cover-preview"
                    />
                  ) : (
                    <span className="text-xs text-slate-400 text-center px-2">
                      Aucune image
                    </span>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <label className="inline-flex">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        data-testid="blog-cover-file-input"
                        onChange={(e) => handleUploadCover(e.target.files?.[0])}
                        disabled={coverUploading || coverGenerating}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={coverUploading || coverGenerating}
                        data-testid="blog-cover-upload-btn"
                        onClick={(e) => e.currentTarget.previousElementSibling.click()}
                      >
                        {coverUploading ? '⏳ Upload...' : '📁 Uploader'}
                      </Button>
                    </label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCoverUrl}
                      disabled={coverUploading || coverGenerating}
                      data-testid="blog-cover-url-btn"
                    >
                      🔗 URL externe
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateCover}
                      disabled={coverUploading || coverGenerating}
                      data-testid="blog-cover-ai-btn"
                    >
                      {coverGenerating ? '⏳ Génération...' : '✨ Générer IA'}
                    </Button>
                    {blogForm.cover_image && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleClearCover}
                        disabled={coverUploading || coverGenerating}
                        data-testid="blog-cover-clear-btn"
                      >
                        🗑️ Retirer
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    JPEG, PNG ou WebP, ≤ 1,5 MB. L&apos;IA utilise le titre de l&apos;article comme sujet — remplis d&apos;abord le titre pour un meilleur rendu.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="content">Contenu *</Label>
              <div className="mt-1">
                <RichTextEditor
                  content={blogForm.content}
                  onChange={(value) => setBlogForm({ ...blogForm, content: value })}
                  placeholder="Rédigez le contenu de votre article..."
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Utilisez la barre d'outils pour formater votre texte</p>
            </div>

            <div>
              <Label htmlFor="tags">Tags (séparés par des virgules)</Label>
              <Input
                id="tags"
                value={blogForm.tags}
                onChange={(e) => setBlogForm({ ...blogForm, tags: e.target.value })}
                placeholder="drone, réglementation, DGAC"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="meta_description">Meta description (SEO)</Label>
              <Input
                id="meta_description"
                value={blogForm.meta_description}
                onChange={(e) => setBlogForm({ ...blogForm, meta_description: e.target.value })}
                placeholder="Description pour les moteurs de recherche"
                maxLength={160}
                className="mt-1"
              />
              <p className="text-xs text-slate-400 mt-1">{blogForm.meta_description.length}/160 caractères</p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="published"
                checked={blogForm.published}
                onChange={(e) => setBlogForm({ ...blogForm, published: e.target.checked })}
                className="w-4 h-4 accent-sky-500"
              />
              <Label htmlFor="published" className="cursor-pointer">Publier immédiatement</Label>
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setBlogDialogOpen(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleSavePost}
                className="flex-1 bg-sky-500 hover:bg-sky-600"
                disabled={!blogForm.title || !blogForm.slug || !blogForm.excerpt || !blogForm.content}
              >
                {editingPost ? 'Mettre à jour' : 'Créer l\'article'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Blog Generation Dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={(open) => { setAiDialogOpen(open); if (!open) setAiBatchResults(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-500" />
              Générer un article avec l'IA
            </DialogTitle>
          </DialogHeader>

          {/* Mode toggle */}
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
            <button
              type="button"
              onClick={() => { setAiMode('single'); setAiBatchResults(null); }}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                aiMode === 'single' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="ai-mode-single"
            >
              Article unique
            </button>
            <button
              type="button"
              onClick={() => { setAiMode('batch'); setAiBatchResults(null); }}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                aiMode === 'batch' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="ai-mode-batch"
            >
              Lot d'articles
            </button>
          </div>

          {/* Catégorie commune aux deux modes */}
          <div>
            <Label className="text-sm font-medium">Catégorie suggérée</Label>
            <Select value={aiCategoryHint} onValueChange={setAiCategoryHint}>
              <SelectTrigger className="mt-1" data-testid="ai-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Réglementation">Réglementation</SelectItem>
                <SelectItem value="Actualités">Actualités</SelectItem>
                <SelectItem value="Tutoriels">Tutoriels</SelectItem>
                <SelectItem value="Sécurité">Sécurité</SelectItem>
                <SelectItem value="International">International</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {aiMode === 'single' ? (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Sujet de l'article</Label>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Ex : Voler avec un drone en Suisse, 10 zones interdites en France, Brevet A2 vs STS-01..."
                  rows={3}
                  className="mt-1"
                  data-testid="ai-prompt-input"
                />
                <p className="text-xs text-slate-500 mt-1">Décrivez le sujet en quelques mots. Notre moteur IA rédigera un article complet (~2500 caractères) en français en environ 20 secondes.</p>
              </div>

              <div className="flex items-start gap-2 bg-violet-50 border border-violet-200 rounded-lg p-3 text-xs text-violet-900">
                <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>L'article sera <strong>généré en brouillon</strong> et ouvert dans l'éditeur. Relisez-le et cliquez sur "Publier" quand vous êtes prêt.</p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAiDialogOpen(false)} disabled={aiGenerating}>
                  Annuler
                </Button>
                <Button
                  onClick={handleGenerateWithAI}
                  disabled={aiGenerating || aiPrompt.trim().length < 5}
                  className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white"
                  data-testid="ai-generate-submit"
                >
                  {aiGenerating ? (
                    <>
                      <span className="animate-spin w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                      Génération...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Générer
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Sujets (1 par ligne — max 15)</Label>
                <Textarea
                  value={aiBatchPrompts}
                  onChange={(e) => setAiBatchPrompts(e.target.value)}
                  placeholder={`Voler avec un drone en Suisse\nVoler avec un drone en Belgique\nVoler avec un drone en Italie\nBrevet A2 : guide complet 2026\nQu'est-ce qu'un NOTAM drone ?`}
                  rows={7}
                  className="mt-1 font-mono text-xs"
                  data-testid="ai-batch-input"
                  disabled={aiGenerating}
                />
                <p className="text-xs text-slate-500 mt-1">
                  {aiBatchPrompts.split('\n').filter(s => s.trim().length >= 5).length} sujet(s) détecté(s). Génération parallèle (3 à la fois), durée ~30-60s pour 5-10 articles.
                </p>
              </div>

              {aiBatchResults && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto" data-testid="ai-batch-results">
                  <div className="text-xs font-semibold text-slate-700 mb-2">
                    Résultats : {aiBatchResults.success}/{aiBatchResults.total} ✅
                  </div>
                  <ul className="space-y-1 text-xs">
                    {aiBatchResults.results.map((r, idx) => (
                      <li key={idx} className={`flex items-start gap-2 ${r.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
                        <span className="flex-shrink-0">{r.ok ? '✅' : '❌'}</span>
                        <span className="truncate">
                          <strong className="font-medium">{r.prompt.slice(0, 50)}{r.prompt.length > 50 ? '…' : ''}</strong>
                          {r.ok && r.saved_post && <span className="text-slate-500"> → /blog/{r.saved_post.slug}</span>}
                          {!r.ok && <span className="text-rose-500"> — {r.error?.slice(0, 80)}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-start gap-2 bg-violet-50 border border-violet-200 rounded-lg p-3 text-xs text-violet-900">
                <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>Tous les articles seront <strong>sauvegardés en brouillon</strong> (non publiés). Vous pourrez les relire et publier individuellement depuis la liste.</p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAiDialogOpen(false)} disabled={aiGenerating}>
                  {aiBatchResults ? 'Fermer' : 'Annuler'}
                </Button>
                <Button
                  onClick={handleGenerateBatch}
                  disabled={aiGenerating || aiBatchPrompts.split('\n').filter(s => s.trim().length >= 5).length === 0}
                  className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white"
                  data-testid="ai-batch-submit"
                >
                  {aiGenerating ? (
                    <>
                      <span className="animate-spin w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                      Génération en cours...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Générer le lot
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Créer un utilisateur (Admin) */}
      <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
        <DialogContent className="max-w-md" data-testid="admin-create-user-dialog">
          <DialogHeader>
            <DialogTitle>Créer un nouvel utilisateur</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 mt-2">
            <div>
              <Label htmlFor="new-user-email">Email *</Label>
              <Input
                id="new-user-email"
                type="email"
                placeholder="admin@drone-maps.eu"
                value={newUserForm.email}
                onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                required
                data-testid="admin-create-user-email"
              />
            </div>
            <div>
              <Label htmlFor="new-user-name">Nom (optionnel)</Label>
              <Input
                id="new-user-name"
                type="text"
                placeholder="Nom complet"
                value={newUserForm.name}
                onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                data-testid="admin-create-user-name"
              />
            </div>
            <div>
              <Label htmlFor="new-user-password">Mot de passe * (min. 8 caractères)</Label>
              <Input
                id="new-user-password"
                type="text"
                placeholder="Mot de passe sécurisé"
                value={newUserForm.password}
                onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                required
                minLength={8}
                data-testid="admin-create-user-password"
              />
              <p className="text-xs text-slate-500 mt-1">
                Astuce : utilise un gestionnaire de mots de passe (1Password, Bitwarden) pour générer un mot de passe fort.
              </p>
            </div>
            <div>
              <Label htmlFor="new-user-role">Rôle</Label>
              <Select
                value={newUserForm.role}
                onValueChange={(v) => setNewUserForm({ ...newUserForm, role: v })}
              >
                <SelectTrigger id="new-user-role" data-testid="admin-create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (accès complet)</SelectItem>
                  <SelectItem value="user">Utilisateur standard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateUserDialogOpen(false)}
                disabled={creatingUser}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={creatingUser}
                className="bg-sky-500 hover:bg-sky-600 text-white"
                data-testid="admin-create-user-submit"
              >
                {creatingUser ? 'Création...' : 'Créer le compte'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
